import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useMismatchWarningRules } from '@/components/ReportForm/hooks/useMismatchWarningRules';
import { useTranslation } from '@/i18n';
import {
  FormDatepicker,
  FormDatepickerProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useGetPopupContainer } from '../Container/PopupContainer';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues> & FormDatepickerProps<TFieldValues>,
  'captionRender'
> & {
  mismatchMessage?: string;
  // `null` opts out of the mismatch captionRender below and falls back to
  // the library's own default caption; `undefined` (the default) keeps it.
  captionRender?: FormDatepickerProps<TFieldValues>['captionRender'] | null;
};

const InnerApReportFormInputInvoiceDueDateField = <
  TFieldValues extends FieldValues
>({
  control,
  name,
  inputId,
  label,
  tooltip,
  hidden,
  caption,
  required = false,
  mismatchMessage,
  captionRender: callerCaptionRender,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();
  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const { rules, captionRender } = useMismatchWarningRules<TFieldValues>(
    name,
    required,
    t('required'),
    mismatchMessage,
    callerCaptionRender
  );

  const getPopupContainer = useGetPopupContainer();
  const datepickerProps: FormDatepickerProps<TFieldValues>['datepickerProps'] =
    useMemo(
      () => ({
        placeholder: t('ap-report-form-due-date-input-placeholder'),
        inputReadOnly: false,
        getPopupContainer,
      }),
      [t, getPopupContainer]
    );

  return (
    <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
      <FormDatepicker<TFieldValues>
        control={control}
        name={name}
        inputId={inputId}
        single
        rules={rules}
        {...rest}
        datepickerProps={datepickerProps}
        captionRender={captionRender}
      />
    </InputBase>
  );
};

InnerApReportFormInputInvoiceDueDateField.displayName =
  'InnerApReportFormInputInvoiceDueDateField';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputInvoiceDueDateField = memo((props) => (
  <InnerApReportFormInputInvoiceDueDateField {...props} />
)) as typeof InnerApReportFormInputInvoiceDueDateField;
ApReportFormInputInvoiceDueDateField.displayName =
  'ApReportFormInputInvoiceDueDateField';
